import React, {useEffect, useState} from "react";
import useAxios from "utils/useAxios";
import { getActas, getActa, getActaTemplate, postActa, putActa } from "api/modules/actas.api";
import { getLocalidades } from "api/modules/localidades.api";
import { getEscuelasByLocalidad } from "api/modules/escuelas.api";

import Spinner from "components/Spinner";
import FormActa from "components/Actas/ActaForm";
import {toast} from "react-toastify";

const ActaList = (props) => {
  const [list, setList] = useState([]);
  const [acta, setActa] = useState({});
  const [actaTemplate, setActaTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('cargar'); // 'cargar' o 'mis-actas'

  // Filtros para Mis Actas (con datos de API)
  const [filtros, setFiltros] = useState({
    localidad: '',
    escuela: '',
    mesa: ''
  });

  // Datos para los selectores
  const [localidades, setLocalidades] = useState([]);
  const [escuelas, setEscuelas] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [isLoadingLocalidades, setIsLoadingLocalidades] = useState(true);
  const [isLoadingEscuelas, setIsLoadingEscuelas] = useState(false);

  const [actasFiltradas, setActasFiltradas] = useState([]);
  const [mostrarResultados, setMostrarResultados] = useState(false);

  const { onSubmit: fetchActas, isSubmitting: isLoading } = useAxios({
    submitFn: getActas,
    onSuccess: ({ data }) => {
      console.log('📊 Actas recibidas de la API:', data);
      setList(data);
    },
  });

  const callFetchActas = () => {
    fetchActas();
  };

  useEffect(callFetchActas, []);

  const { onSubmit: fetchTemplate } = useAxios({
    submitFn: getActaTemplate,
    onSuccess: ({ data }) => {
      setActaTemplate(data);
      setActa(data);
    },
  });

  const callFetchListas = () => {
    fetchTemplate();
  };

  useEffect(callFetchListas, []);

  // Obtener localidades para los filtros
  const { onSubmit: fetchLocalidades } = useAxios({
    submitFn: getLocalidades,
    onSuccess: (response) => {
      try {
        const localidadesArray = Array.isArray(response?.data?.data) ? response.data.data : [];
        setLocalidades(localidadesArray);
        setIsLoadingLocalidades(false);
      } catch (error) {
        console.error('❌ Error procesando localidades:', error);
        setLocalidades([]);
        setIsLoadingLocalidades(false);
      }
    },
    onError: (error) => {
      console.error('❌ Error al obtener localidades:', error);
      setLocalidades([]);
      setIsLoadingLocalidades(false);
    }
  });

  // Obtener escuelas por localidad para los filtros
  const { onSubmit: fetchEscuelas } = useAxios({
    submitFn: getEscuelasByLocalidad,
    onSuccess: (response) => {
      try {
        const escuelasArray = Array.isArray(response?.data) ? response.data : [];
        setEscuelas(escuelasArray);
        setIsLoadingEscuelas(false);
      } catch (error) {
        console.error('❌ Error procesando escuelas:', error);
        setEscuelas([]);
        setIsLoadingEscuelas(false);
      }
    },
    onError: (error) => {
      console.error('❌ Error al obtener escuelas:', error);
      setEscuelas([]);
      setIsLoadingEscuelas(false);
    }
  });

  // Cargar localidades al montar el componente
  useEffect(() => {
    fetchLocalidades();
  }, []);

  // Cargar escuelas cuando cambie la localidad seleccionada
  useEffect(() => {
    if (filtros.localidad) {
      setIsLoadingEscuelas(true);
      fetchEscuelas(filtros.localidad);
    } else {
      setEscuelas([]);
      setMesas([]);
    }
  }, [filtros.localidad]);

  // Generar mesas cuando cambie la escuela seleccionada
  useEffect(() => {
    if (filtros.escuela && escuelas.length > 0) {
      const escuela = escuelas.find(e => e.id === parseInt(filtros.escuela));
      if (escuela && escuela.min_mesa && escuela.max_mesa) {
        const mesasArray = [];
        for (let i = escuela.min_mesa; i <= escuela.max_mesa; i++) {
          mesasArray.push(i);
        }
        setMesas(mesasArray);
      } else {
        setMesas([]);
      }
    } else {
      setMesas([]);
    }
  }, [filtros.escuela, escuelas]);

  const { onSubmit, isSubmitting, errors } = useAxios({
    submitFn: (form) => acta.id ? putActa(acta.id, form) : postActa(form),
    onSuccess: () => {
      toast.success("Acta enviada correctamente");
      fetchActas();
      // No llamamos onClick(actaTemplate) aquí, se maneja en el ActaForm
    }
  });

  const { onSubmit: fetchActa } = useAxios({
    submitFn: getActa,
    onSuccess: ({ data }) => {
      setActa(data);
      window.scrollTo(0, 0);
    },
  });

  const onClick = acta => {
    if (acta.id) {
      // Si es un acta existente, obtener los datos completos
      fetchActa(acta.id);
    } else {
      // Si es el template, usar directamente
      setActa(acta);
      window.scrollTo(0, 0);
    }
  }

  const onCancel = () => onClick(actaTemplate);

  const onFormReset = () => {
    // Cuando el formulario se resetea exitosamente, volver al template
    onClick(actaTemplate);
  };

  // Función para buscar actas con filtros
  const buscarActas = () => {
    if (!filtros.localidad && !filtros.escuela && !filtros.mesa) {
      toast.warning("Por favor, selecciona al menos un filtro para buscar");
      return;
    }

    const filtradas = list.filter(acta => {
      // Filtros combinados: solo aplicamos los filtros que están seleccionados
      const matchLocalidad = !filtros.localidad || 
        (acta.localidad && acta.localidad.toString() === filtros.localidad);
      const matchEscuela = !filtros.escuela || 
        (acta.escuela && acta.escuela.toString() === filtros.escuela);
      const matchMesa = !filtros.mesa || 
        (acta.mesa && acta.mesa.toString() === filtros.mesa);

      // Solo debe cumplir con los filtros que están activos
      return matchLocalidad && matchEscuela && matchMesa;
    });

    setActasFiltradas(filtradas);
    setMostrarResultados(true);

    if (filtradas.length === 0) {
      toast.info("No se encontraron actas con los filtros especificados");
    } else {
      const filtrosActivos = [];
      if (filtros.localidad) filtrosActivos.push(`Localidad: ${filtros.localidad}`);
      if (filtros.escuela) filtrosActivos.push(`Escuela: ${filtros.escuela}`);
      if (filtros.mesa) filtrosActivos.push(`Mesa: ${filtros.mesa}`);

      toast.success(`Se encontraron ${filtradas.length} actas con filtros: ${filtrosActivos.join(', ')}`);
    }
  };

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({ localidad: '', escuela: '', mesa: '' });
    setActasFiltradas([]);
    setMostrarResultados(false);
  };

  // Función para editar acta desde la lista
  const editarActa = (acta) => {
    console.log('🔍 ListActas - Datos del acta que se va a editar:', acta);
    console.log('🔍 ListActas - Campos específicos:', {
      id: acta.id,
      localidad: acta.localidad,
      escuela: acta.escuela,
      mesa: acta.mesa,
      distrito: acta.distrito
    });
    onClick(acta);
    setActiveTab('cargar');
  };

  return (
    <div>
      {/* Pestañas */}
      <ul className="nav nav-tabs mb-4" id="actasTabs" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'cargar' ? 'active' : ''}`}
            onClick={() => setActiveTab('cargar')}
            type="button"
            role="tab"
          >
            <i className="fas fa-plus-circle me-2"></i>
            Cargar Acta
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'mis-actas' ? 'active' : ''}`}
            onClick={() => setActiveTab('mis-actas')}
            type="button"
            role="tab"
          >
            <i className="fas fa-list me-2"></i>
            Mis Actas
          </button>
        </li>
      </ul>

      {/* Contenido de las pestañas */}
      <div className="tab-content" id="actasTabsContent">
        {/* Pestaña: Cargar Acta */}
        <div className={`tab-pane fade ${activeTab === 'cargar' ? 'show active' : ''}`} role="tabpanel">
          <h3 className="d-flex justify-content-between">
            {acta.id ? 'Editar' : 'Cargar'} acta
            {acta.id ? <button className="btn btn-outline-secondary" onClick={onCancel}>Cancelar</button> : null}
          </h3>
          {actaTemplate ? (
            <FormActa
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
              errors={errors}
              acta={acta}
              onCancel={onCancel}
              actasCargadas={list}
              onFormReset={onFormReset}
            />
          ) : (
            <Spinner />
          )}
        </div>

        {/* Pestaña: Mis Actas */}
        <div className={`tab-pane fade ${activeTab === 'mis-actas' ? 'show active' : ''}`} role="tabpanel">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                <i className="fas fa-search me-2"></i>
                Buscar Mis Actas
              </h4>
            </div>
            <div className="card-body">
              {/* Filtros con selectores de API */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                    Localidad
                  </label>
                  <select
                    className="form-select location-filter-select"
                    value={filtros.localidad}
                    onChange={(e) => setFiltros({ ...filtros, localidad: e.target.value, escuela: '', mesa: '' })}
                    disabled={isLoadingLocalidades}
                  >
                    <option value="">
                      {isLoadingLocalidades ? 'Cargando...' : 'Seleccionar localidad'}
                    </option>
                    {localidades.map(localidad => (
                      <option key={localidad.id} value={localidad.id}>
                        {localidad.circuito} - {localidad.descripcion}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-school me-2 text-success"></i>
                    Escuela
                  </label>
                  <select
                    className="form-select location-filter-select"
                    value={filtros.escuela}
                    onChange={(e) => setFiltros({ ...filtros, escuela: e.target.value, mesa: '' })}
                    disabled={!filtros.localidad || isLoadingEscuelas}
                  >
                    <option value="">
                      {!filtros.localidad ? 'Selecciona localidad primero' :
                        isLoadingEscuelas ? 'Cargando...' : 'Seleccionar escuela'}
                    </option>
                    {escuelas.map(escuela => (
                      <option key={escuela.id} value={escuela.id}>
                        {escuela.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-list-ol me-2 text-info"></i>
                    Mesa
                  </label>
                  <select
                    className="form-select location-filter-select"
                    value={filtros.mesa}
                    onChange={(e) => setFiltros({ ...filtros, mesa: e.target.value })}
                    disabled={!filtros.escuela}
                  >
                    <option value="">
                      {!filtros.escuela ? 'Selecciona escuela primero' : 'Seleccionar mesa'}
                    </option>
                    {mesas.map(mesa => (
                      <option key={mesa} value={mesa}>
                        {mesa}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="row mb-4">
                <div className="col-12 text-center">
                  <button
                    className="btn btn-primary btn-lg me-3"
                    onClick={buscarActas}
                    disabled={isLoading}
                  >
                    <i className="fas fa-search me-2"></i>
                    {isLoading ? 'Buscando...' : 'Buscar Actas'}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-lg"
                    onClick={limpiarFiltros}
                  >
                    <i className="fas fa-eraser me-2"></i>
                    Limpiar Filtros
                  </button>
                </div>
              </div>

              {/* Resultados */}
              {mostrarResultados && (
                <div className="mt-4">
                  <h5 className="text-success mb-3">
                    <i className="fas fa-list me-2"></i>
                    Resultados de la búsqueda ({actasFiltradas.length} actas)
                  </h5>

                  {actasFiltradas.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead className="table-dark">
                          <tr>
                            <th>Localidad</th>
                            <th>Escuela</th>
                            <th>Mesa</th>
                            <th>Electores</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actasFiltradas.map((acta) => (
                            <tr key={acta.id}>
                              <td>{acta.localidad || 'N/A'}</td>
                              <td>{acta.escuela_nombre || 'N/A'}</td>
                              <td>{acta.mesa || 'N/A'}</td>
                              <td>{acta.total_votantes_padron || 0}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => editarActa(acta)}
                                  title="Editar acta"
                                >
                                  Editar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <i className="fas fa-search fa-3x mb-3"></i>
                      <p>No se encontraron actas con los filtros especificados</p>
                    </div>
                  )}
                </div>
              )}

              {/* Estado inicial */}
              {!mostrarResultados && (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-search fa-4x mb-3"></i>
                  <h5>Busca tus actas</h5>
                  <p>Usa los filtros de arriba para buscar actas por localidad, escuela o mesa</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActaList;
